import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users,
  Target,
  Award,
  Heart,
  ArrowRight,
  Shield,
  Bot,
  Star,
  CheckCircle,
} from "lucide-react";

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Results-Driven",
      description: "We focus on measurable outcomes. Your improved reputation and increased business is our success metric.",
    },
    {
      icon: Shield,
      title: "Confidentiality",
      description: "Your reputation challenges are handled with complete discretion. We protect your privacy at every step.",
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Google Platinum Partner status reflects our commitment to delivering exceptional marketing results.",
    },
    {
      icon: Heart,
      title: "Client Success",
      description: "We're invested in your long-term success, not just quick fixes. Your reputation is our reputation.",
    },
  ];

  const team = [
    {
      name: "William Thompson",
      role: "CEO & Founder",
      bio: "15+ years in reputation management and digital marketing. Former VP at a Fortune 500 PR firm.",
    },
    {
      name: "Sarah Chen",
      role: "CTO",
      bio: "AI and machine learning expert. Built our proprietary monitoring system that tracks 50+ platforms.",
    },
    {
      name: "Michael Rodriguez",
      role: "Director of Client Success",
      bio: "Dedicated to ensuring every client sees measurable improvement in their online reputation.",
    },
    {
      name: "Emily Park",
      role: "Head of Marketing",
      bio: "Google Ads certified expert. Leads our A/B testing program with 5000+ optimization parameters.",
    },
  ];

  const certifications = [
    "Google Platinum Partner",
    "BBB A+ Rating",
    "SOC 2 Compliant",
    "GDPR Compliant",
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero */}
      <section className="hero-gradient py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F37021]/10 text-[#F37021] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Users className="h-4 w-4" />
                About Us
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground mb-6"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                We Make You{" "}
                <span className="gradient-text">Look Good Online</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                5 Star Marketing is a results-driven reputation management company helping 
                businesses and professionals take control of their online image. Powered by AI, 
                backed by expertise.
              </p>
              <div className="flex flex-wrap gap-8">
                <div>
                  <p className="text-3xl font-bold text-foreground">15+</p>
                  <p className="text-sm text-muted-foreground">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">500+</p>
                  <p className="text-sm text-muted-foreground">Happy Clients</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">98%</p>
                  <p className="text-sm text-muted-foreground">Success Rate</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#F37021]/20 to-[#F37021]/5 rounded-2xl p-8 lg:p-12">
                <div className="space-y-4">
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex items-center gap-3 bg-card rounded-lg p-4 shadow-sm">
                      <Award className="h-6 w-6 text-[#F37021]" />
                      <span className="font-medium text-foreground">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-28 section-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2
                className="text-3xl sm:text-4xl font-bold text-foreground mb-6"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Our Story
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  5 Star Marketing was founded with a simple mission: help businesses and 
                  professionals take control of their online reputation. We saw too many good 
                  people and companies unfairly damaged by negative online content.
                </p>
                <p>
                  What sets us apart is our AI-powered approach. While other reputation companies 
                  rely on manual monitoring, our AI agents work 24/7 across 50+ platforms, catching 
                  issues before they become problems and responding faster than any human team.
                </p>
                <p>
                  Today, we've helped hundreds of clients improve their Google, Yelp, Avvo, Vitals, 
                  and other platform ratings. Our Google Platinum Partner status reflects our 
                  expertise in not just protecting reputations, but actively promoting positive 
                  content through strategic digital marketing.
                </p>
              </div>
            </div>
            <Card className="bg-[#F37021] border-none">
              <CardContent className="p-8">
                <Bot className="h-12 w-12 text-white mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                  The AI Advantage
                </h3>
                <p className="text-white/90 mb-6">
                  Our proprietary AI system monitors reviews in real-time, analyzes sentiment, 
                  and helps craft responses that protect your brand. We test 5000+ parameters 
                  to optimize your marketing campaigns.
                </p>
                <div className="space-y-3">
                  {[
                    "24/7 automated monitoring",
                    "Instant alerts on new reviews",
                    "AI-powered response suggestions",
                    "5000+ A/B test parameters",
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-white">
                      <CheckCircle className="h-5 w-5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 section-muted">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Our Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card
                key={value.title}
                data-testid={`value-${index}`}
                className="card-hover bg-card border-border text-center"
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-[#F37021]/10 flex items-center justify-center mx-auto mb-6">
                    <value.icon className="h-7 w-7 text-[#F37021]" />
                  </div>
                  <h3
                    className="text-xl font-bold text-foreground mb-3"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 lg:py-28 section-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Meet Our Team
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experienced professionals dedicated to protecting your reputation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <Card
                key={member.name}
                data-testid={`team-member-${index}`}
                className="card-hover bg-card border-border text-center"
              >
                <CardContent className="p-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F37021] to-[#FF9B5C] flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                    {member.name.split(" ").map(n => n[0]).join("")}
                  </div>
                  <h3 className="font-bold text-foreground">{member.name}</h3>
                  <p className="text-sm text-[#F37021] mb-3">{member.role}</p>
                  <p className="text-sm text-muted-foreground">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 lg:py-28 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="flex justify-center mb-6">
            {[1,2,3,4,5].map((i) => (
              <Star key={i} className="h-8 w-8 fill-[#F37021] text-[#F37021]" />
            ))}
          </div>
          <blockquote className="text-2xl lg:text-3xl text-white font-medium mb-8 italic">
            "5 Star Marketing transformed our online presence. Our Google reviews went from 3.2 to 4.8 stars, 
            and we've seen a 40% increase in new patient inquiries."
          </blockquote>
          <div className="flex items-center justify-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#F37021] to-[#FF9B5C] flex items-center justify-center text-white font-bold text-lg">
              DM
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">Dr. Michelle Torres</p>
              <p className="text-gray-400">Dental Practice Owner</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#F37021] to-[#D95F18]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Ready to Protect Your Reputation?
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10">
            Get a free consultation and see how we can help improve your online presence.
          </p>
          <Link to="/contact">
            <Button
              data-testid="about-cta-btn"
              size="lg"
              className="bg-white text-[#F37021] hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold"
            >
              Get Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
